import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AtSign, Clock, Loader2, Search, SquarePen, UsersRound, X } from "lucide-react";
import { connectParityApi, type ChatListItem } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useChats } from "@/api/hooks";
import { cn } from "@/lib/utils";
import { NewGroupDialog } from "../NewGroupDialog";
import { noteText, usePeerStatus, type PeerNote } from "../presence/PeerStatus";
import { MeetingChatThread } from "../meeting/MeetingChatThread";
import { ChatThread } from "./ChatThread";
import { ChatAvatar, listTime, presenceLabel } from "./chatUi";

/** "dm:<id>" / "group:<id>" → who is typing there (user id → name + when). */
export type ChatTypingMap = Record<string, Record<string, { name: string; at: number }>>;

/** A typing signal older than this is dropped, in case the "stopped" event was missed. */
const TYPING_TTL_MS = 6000;

/** The list's quick filters: everything, chats with unread messages, meetings' chats. */
type ChatFilter = "all" | "unread" | "meetings";

/** A card on the canvas (suite-shell mock-up): white, 8px corners, soft shadow. */
const CARD = "overflow-hidden rounded-lg bg-card text-card-foreground shadow-[var(--connect-shadow-4)]";

/**
 * Teams' Chat: one list for 1:1 and group chats, most recent first, with a
 * preview, time, unread badge, presence and "typing…" — replacing separate
 * Chats and Groups views that showed only names. The open chat lives in the
 * URL (?chat=dm:<id>), so Back works, links can be shared and a refresh
 * keeps you where you were.
 *
 * Laid out as the approved suite-shell mock-up: the list and the open chat
 * are two cards on the canvas; the list has All / Unread / Meetings chips.
 */
export function ChatsView({ initialChatId, typing, onTyping }: {
    /** A DM the shell wants open (e.g. picked from the top-bar people search). */
    initialChatId?: string | null;
    typing: ChatTypingMap;
    onTyping: (kind: "dm" | "group", chatId: string, started: boolean) => void;
}) {
    const chats = useChats();
    const token = useAccessToken();
    const qc = useQueryClient();
    const [params, setParams] = useSearchParams();
    const selectedKey = params.get("chat");
    const [filter, setFilter] = useState("");
    const [view, setView] = useState<ChatFilter>("all");
    const [pickerOpen, setPickerOpen] = useState(false);
    const [showNewGroup, setShowNewGroup] = useState(false);
    const [, setTick] = useState(0);

    // Presence + status messages: the tenant snapshot overlaid with live hub updates.
    const { presenceOf, noteOf } = usePeerStatus();

    const list = useMemo(() => chats.data ?? [], [chats.data]);
    const unreadChats = useMemo(() => list.filter((c) => c.unreadCount > 0).length, [list]);
    const hasMeetings = useMemo(() => list.some((c) => c.kind === "meeting"), [list]);
    const visible = useMemo(() => {
        const q = filter.trim().toLowerCase();
        return list.filter((c) => {
            // The open chat stays listed under Unread once it's been read.
            const inView = view === "unread"
                ? c.unreadCount > 0 || `${c.kind}:${c.id}` === selectedKey
                : view === "meetings" ? c.kind === "meeting" : true;
            if (!inView) return false;
            if (!q) return true;
            return c.title.toLowerCase().includes(q)
                || (c.peerEmail ?? "").toLowerCase().includes(q)
                || c.memberNames.some((n) => n.toLowerCase().includes(q));
        });
    }, [list, filter, view, selectedKey]);
    const selected = selectedKey ? list.find((c) => `${c.kind}:${c.id}` === selectedKey) ?? null : null;

    const select = useCallback((key: string | null, replace = false) => {
        const next = new URLSearchParams(params);
        if (key) next.set("chat", key);
        else next.delete("chat");
        setParams(next, { replace });
    }, [params, setParams]);

    // On a wide screen open the most recent chat once, as Teams does.
    const didAutoSelect = useRef(false);
    useEffect(() => {
        if (didAutoSelect.current || selectedKey || list.length === 0) return;
        didAutoSelect.current = true;
        if (window.matchMedia("(min-width: 768px)").matches) select(`${list[0]!.kind}:${list[0]!.id}`, true);
    }, [list, selectedKey, select]);

    // Opened from elsewhere (top-bar people search) — the chat may be brand new.
    useEffect(() => {
        if (!initialChatId) return;
        didAutoSelect.current = true;
        void qc.invalidateQueries({ queryKey: ["chats"] });
        select(`dm:${initialChatId}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialChatId]);

    const typingFor = (key: string) => {
        const now = Date.now();
        return Object.values(typing[key] ?? {}).filter((t) => now - t.at < TYPING_TTL_MS).map((t) => t.name);
    };
    // Re-render while someone is typing so stale entries age out.
    const anyTyping = Object.values(typing).some((m) => Object.values(m).some((t) => Date.now() - t.at < TYPING_TTL_MS));
    useEffect(() => {
        if (!anyTyping) return;
        const t = window.setInterval(() => setTick((n) => n + 1), 2000);
        return () => window.clearInterval(t);
    }, [anyTyping]);

    const startDm = async (userId: string) => {
        if (!token) return;
        const chat = await connectParityApi.startDirectChat(userId, token);
        setPickerOpen(false);
        await qc.invalidateQueries({ queryKey: ["chats"] });
        select(`dm:${chat.id}`);
    };

    const emptyForView = filter.trim()
        ? `No chats match “${filter}”.`
        : view === "unread" ? "You're all caught up — no unread chats." : "No meeting chats.";

    return (
        <div className="flex min-h-0 min-w-0 flex-1 gap-2">
            <aside
                aria-label="Chats"
                className={cn(
                    CARD,
                    "w-full shrink-0 flex-col md:flex md:w-80",
                    // Master/detail on phones: the list is the screen until a chat is open.
                    selectedKey ? "hidden" : "flex"
                )}
            >
                <header className="flex shrink-0 items-center gap-1 pb-1.5 pl-3.5 pr-2.5 pt-2.5">
                    <h2 className="flex-1 text-base font-semibold leading-[22px]">Chats</h2>
                    <IconButton label="New chat" onClick={() => setPickerOpen((v) => !v)} active={pickerOpen}>
                        <SquarePen className="h-4 w-4" />
                    </IconButton>
                    <IconButton label="New group chat" onClick={() => setShowNewGroup(true)}>
                        <UsersRound className="h-4 w-4" />
                    </IconButton>
                </header>

                {pickerOpen ? (
                    <PersonPicker onPick={(id) => void startDm(id)} onClose={() => setPickerOpen(false)} />
                ) : null}

                <div className="shrink-0 px-3 pb-2 pl-3.5">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--connect-text-3)]" aria-hidden />
                        <input
                            type="search"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Search chats"
                            aria-label="Search chats"
                            className="h-8 w-full rounded border border-[var(--connect-line-strong)] bg-card pl-8 pr-2 text-sm outline-none placeholder:text-[var(--connect-text-3)] focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                    </div>
                </div>

                <div role="group" aria-label="Show" className="flex shrink-0 flex-wrap gap-1.5 pb-2 pl-3.5 pr-3">
                    <FilterChip on={view === "all"} onClick={() => setView("all")}>All</FilterChip>
                    <FilterChip
                        on={view === "unread"}
                        onClick={() => setView("unread")}
                        label={unreadChats > 0 ? `Unread, ${unreadChats} chat${unreadChats === 1 ? "" : "s"}` : "Unread"}
                    >
                        Unread
                        {unreadChats > 0 ? <CountPill value={unreadChats} /> : null}
                    </FilterChip>
                    {hasMeetings || view === "meetings" ? (
                        <FilterChip on={view === "meetings"} onClick={() => setView("meetings")}>Meetings</FilterChip>
                    ) : null}
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto pb-2">
                    {visible.length > 0 ? (
                        <div className="flex items-center gap-1.5 px-3.5 pb-1 pt-1.5 text-xs font-semibold text-[var(--connect-text-2)]" aria-hidden>
                            <Clock className="h-3.5 w-3.5" />
                            {view === "unread" ? "Unread" : view === "meetings" ? "Meetings" : "Recent"}
                        </div>
                    ) : null}
                    <ul role="list">
                        {chats.isPending ? (
                            <li className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading chats…
                            </li>
                        ) : chats.isError ? (
                            <li className="px-4 py-3 text-sm text-destructive">Couldn't load your chats. Retrying…</li>
                        ) : list.length === 0 ? (
                            <li className="px-4 py-6 text-center text-sm text-muted-foreground">
                                No chats yet. Start one with <span className="font-medium text-foreground">New chat</span>.
                            </li>
                        ) : visible.length === 0 ? (
                            <li className="px-4 py-3 text-sm text-muted-foreground">{emptyForView}</li>
                        ) : (
                            visible.map((c) => {
                                const key = `${c.kind}:${c.id}`;
                                return (
                                    <ChatRow
                                        key={key}
                                        chat={c}
                                        active={key === selectedKey}
                                        presence={c.kind === "dm" ? presenceOf(c.peerUserId) : undefined}
                                        note={c.kind === "dm" ? noteOf(c.peerUserId) : null}
                                        typingNames={typingFor(key)}
                                        onSelect={() => select(key)}
                                    />
                                );
                            })
                        )}
                    </ul>
                </div>
            </aside>

            <div className={cn(CARD, "min-h-0 min-w-0 flex-1 md:flex", selectedKey ? "flex" : "hidden")}>
                {selected?.kind === "meeting" ? (
                    // A meeting's chat, kept after the meeting as in Teams.
                    <MeetingChatThread
                        key={selectedKey}
                        meetingId={selected.id}
                        title={selected.title}
                        variant="page"
                        onBack={() => select(null)}
                    />
                ) : selected ? (
                    <ChatThread
                        key={selectedKey}
                        chat={selected}
                        presence={selected.kind === "dm" ? presenceOf(selected.peerUserId) : undefined}
                        typingNames={typingFor(selectedKey!)}
                        onTyping={(started) => onTyping(selected.kind === "dm" ? "dm" : "group", selected.id, started)}
                        onBack={() => select(null)}
                    />
                ) : selectedKey && chats.isFetching ? (
                    <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Opening chat…
                    </div>
                ) : (
                    <div className="hidden flex-1 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground md:flex">
                        <SquarePen className="h-8 w-8 opacity-40" aria-hidden />
                        Pick a chat, or start a new one.
                    </div>
                )}
            </div>

            {showNewGroup ? (
                <NewGroupDialog
                    onClose={() => setShowNewGroup(false)}
                    onCreated={(id) => {
                        setShowNewGroup(false);
                        void qc.invalidateQueries({ queryKey: ["chats"] }).then(() => select(`group:${id}`));
                    }}
                />
            ) : null}
        </div>
    );
}

function ChatRow({ chat, active, presence, note, typingNames, onSelect }: {
    chat: ChatListItem;
    active: boolean;
    presence?: string;
    /** DM only: the other person's status message, shown in the tooltip. */
    note: PeerNote | null;
    typingNames: string[];
    onSelect: () => void;
}) {
    const unread = chat.unreadCount > 0;
    const mentions = chat.mentionCount ?? 0;
    const tooltip = chat.kind === "dm"
        ? [chat.title, presenceLabel(presence), noteText(note)].filter(Boolean).join(" · ")
        : undefined;
    const last = chat.lastMessage;
    const preview = typingNames.length > 0
        ? "typing…"
        : last
            ? `${last.mine ? "You: " : chat.kind !== "dm" && last.authorName ? `${last.authorName.split(" ")[0]}: ` : ""}${last.snippet || "Attachment"}`
            : "No messages yet";
    return (
        <li className="px-1.5">
            <button
                type="button"
                onClick={onSelect}
                aria-current={active ? "true" : undefined}
                aria-label={`${chat.title}${unread ? `, ${chat.unreadCount} unread` : ""}${mentions > 0 ? ", you were mentioned" : ""}${note ? `, status: ${noteText(note)}` : ""}`}
                title={tooltip}
                className={cn(
                    "grid w-full grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-md py-2 pl-2 pr-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                    active ? "bg-[var(--connect-selected)]" : "hover:bg-[var(--connect-hover)]"
                )}
            >
                <ChatAvatar name={chat.title} group={chat.kind === "group"} meeting={chat.kind === "meeting"} presence={presence} size="list" />
                <span className="min-w-0">
                    <span className={cn("block truncate text-[13.5px] leading-5", unread && "font-bold")}>{chat.title}</span>
                    <span className={cn(
                        "block truncate text-[12.5px] leading-[18px]",
                        typingNames.length > 0 ? "italic text-primary" : unread ? "font-semibold text-foreground" : "text-[var(--connect-text-2)]"
                    )}>
                        {preview}
                    </span>
                </span>
                <span className="flex flex-col items-end gap-[3px] text-right text-[11px] text-[var(--connect-text-3)]">
                    <span className="tabular-nums">{last ? listTime(last.sentAt) : ""}</span>
                    {mentions > 0 || unread ? (
                        <span className="flex items-center gap-1">
                            {mentions > 0 ? (
                                <span
                                    className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-rose-600 text-white"
                                    title={`${mentions} unread mention${mentions === 1 ? "" : "s"}`}
                                    aria-hidden
                                >
                                    <AtSign className="h-2.5 w-2.5" />
                                </span>
                            ) : null}
                            {unread ? <CountPill value={chat.unreadCount} /> : null}
                        </span>
                    ) : null}
                </span>
            </button>
        </li>
    );
}

/** The mock-up's count pill: accent text on an accent-tinted fill. */
function CountPill({ value }: { value: number }) {
    return (
        <span className="rounded-lg bg-[var(--connect-primary-soft)] px-1.5 text-[11px] font-semibold leading-4 tabular-nums text-primary" aria-hidden>
            {value > 99 ? "99+" : value}
        </span>
    );
}

function FilterChip({ on, onClick, label, children }: {
    on: boolean;
    onClick: () => void;
    /** Accessible name when the visible text carries a count. */
    label?: string;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={on}
            aria-label={label}
            className={cn(
                "inline-flex h-7 items-center gap-1.5 rounded border px-2.5 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                on
                    ? "border-primary/45 bg-[var(--connect-primary-soft)] font-semibold text-primary"
                    : "border-[var(--connect-line-strong)] bg-card hover:bg-[var(--connect-hover)]"
            )}
        >
            {children}
        </button>
    );
}

function PersonPicker({ onPick, onClose }: { onPick: (userId: string) => void; onClose: () => void }) {
    const token = useAccessToken();
    const [q, setQ] = useState("");
    const results = useQuery({
        queryKey: ["connect-dir-search", q, token],
        queryFn: () => connectParityApi.searchDirectory(q.trim(), token),
        enabled: Boolean(token && q.trim().length >= 2)
    });
    return (
        <div className="space-y-2 border-y border-[var(--connect-line)] bg-[var(--connect-pane)] p-3">
            <div className="flex items-center gap-2">
                <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                    <input
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
                        placeholder="To: name or email"
                        aria-label="Find someone to chat with"
                        className="h-8 w-full rounded border border-[var(--connect-line-strong)] bg-card pl-8 pr-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                </div>
                <IconButton label="Close" onClick={onClose}>
                    <X className="h-4 w-4" />
                </IconButton>
            </div>
            {q.trim().length < 2 ? (
                <p className="px-1 text-xs text-muted-foreground">Type at least 2 letters.</p>
            ) : results.isFetching && !results.data ? (
                <p className="flex items-center gap-2 px-1 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Searching…</p>
            ) : (results.data ?? []).length === 0 ? (
                <p className="px-1 text-xs text-muted-foreground">No one matches.</p>
            ) : (
                <ul className="max-h-56 overflow-y-auto">
                    {(results.data ?? []).map((u) => (
                        <li key={u.userId}>
                            <button
                                type="button"
                                onClick={() => onPick(u.userId)}
                                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-[var(--connect-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            >
                                <ChatAvatar name={u.displayName ?? u.email} size="sm" />
                                <span className="min-w-0">
                                    <span className="block truncate text-sm">{u.displayName ?? u.email}</span>
                                    <span className="block truncate text-[11px] text-muted-foreground">{u.email}</span>
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function IconButton({ label, onClick, active, children }: {
    label: string;
    onClick: () => void;
    active?: boolean;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            aria-pressed={active}
            className={cn(
                "flex h-8 w-8 items-center justify-center rounded hover:bg-[var(--connect-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                active && "bg-[var(--connect-selected)] text-primary"
            )}
        >
            {children}
        </button>
    );
}
